import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import JourneyLadder from '../../components/JourneyLadder';
import { useAuth } from '../../context/AuthContext';
import { useAppConfig } from '../../hooks/useAppConfig';
import { useLanguages } from '../../hooks/useLanguages';
import { LearningProgressService } from '../../services/LearningProgressService';

const STARTED_ONLY_STEPS = new Set(['foundations', 'basics', 'alphabet', 'grammar', 'practice']);

function routeForStep(code, targetCode) {
  const routes = {
    foundations: '/learn/common-foundations',
    basics: targetCode ? `/learn/${targetCode}` : '/',
    alphabet: targetCode ? `/learn/${targetCode}?tab=alphabet` : '/',
    vocabulary: '/learn/vocabulary',
    grammar: '/learn/grammar',
    conversations: '/learn/conversations',
    stories: '/learn/stories',
    writing: '/learn/writing',
    listen: targetCode ? `/listen/${targetCode}` : '/',
    speak: '/speak',
    practice: '/practice',
    tests: '/exercises',
  };
  return routes[code] || '/';
}

export default function SkillPathPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { languagePrefs } = useAuth();
  const config = useAppConfig(languagePrefs?.nativeLanguage);
  const { languages } = useLanguages();
  const targetCode = languagePrefs?.targetLanguage || '';
  const langPair = languagePrefs
    ? `${languagePrefs.nativeLanguage}_${targetCode}`
    : 'guest';
  const targetLabel = languages.find((language) => language.code === targetCode)?.label
    || targetCode.toUpperCase()
    || t('learningPaths.aLanguage');
  const [progress, setProgress] = useState(() => LearningProgressService.snapshot(langPair));

  useEffect(() => {
    const refresh = () => setProgress(LearningProgressService.snapshot(langPair));
    refresh();
    window.addEventListener('learning-progress-changed', refresh);
    return () => window.removeEventListener('learning-progress-changed', refresh);
  }, [langPair]);

  const items = useMemo(() => config.learningSteps.map((step) => ({
    ...step,
    completed: (progress.steps[step.code]?.percent || 0) >= 100,
  })), [config.learningSteps, progress.steps]);
  const nextStep = items.find((step) => !step.completed) || items[0];

  const openStep = (step) => {
    if (STARTED_ONLY_STEPS.has(step.code)) {
      LearningProgressService.record(langPair, step.code, 1, {
        activityId: `started:${step.code}`,
      });
    }
    navigate(routeForStep(step.code, targetCode));
  };

  const finishStep = (step) => {
    setProgress(LearningProgressService.completeStep(langPair, step.code));
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 via-white to-violet-50 px-4 py-8 sm:px-6 sm:py-10 lg:px-8">
      <main className="mx-auto max-w-5xl">
        <Link to="/" className="inline-flex items-center gap-2 text-sm font-black text-indigo-700 hover:text-indigo-900">
          <span aria-hidden="true">←</span> {t('journeyPages.backHome')}
        </Link>

        <section className="mt-6 overflow-hidden rounded-3xl bg-gradient-to-br from-blue-700 via-indigo-700 to-violet-700 p-7 text-white shadow-xl sm:p-10">
          <p className="text-xs font-black uppercase tracking-[0.2em] text-white/70">{t('learningPaths.skillTitle')}</p>
          <h1 className="mt-3 text-3xl font-black sm:text-5xl">{config.learningPathTitle || t('journeyPages.skillHeading', { language: targetLabel })}</h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-white/85 sm:text-base">{config.learningPathSubtitle || t('learningPaths.skillDesc')}</p>
          <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="min-w-0 flex-1">
              <div className="flex justify-between text-xs font-black"><span>{t('journeyPages.overallProgress')}</span><span>{progress.overall}%</span></div>
              <div className="mt-2 h-3 overflow-hidden rounded-full bg-white/20"><div className="h-full rounded-full bg-emerald-400 transition-all" style={{ width: `${progress.overall}%` }} /></div>
            </div>
            {nextStep && (
              <button type="button" onClick={() => openStep(nextStep)} className="rounded-xl bg-white px-5 py-3 text-sm font-black text-indigo-700 shadow-md transition hover:-translate-y-0.5">
                {progress.overall === 100 ? t('journeyPages.revisitPath') : t('journeyPages.continueJourney')}
              </button>
            )}
          </div>
        </section>

        {items.length === 0 ? (
          <div className="mt-8 space-y-5" aria-label={t('journeyPages.loadingJourney')}>
            {[1, 2, 3].map((item) => <div key={item} className="h-36 animate-pulse rounded-3xl bg-white" />)}
          </div>
        ) : (
          <section className="mt-10">
            <JourneyLadder
              items={items}
              ariaLabel={t('journeyPages.skillLadder')}
              renderItem={(step) => {
                const stepProgress = progress.steps[step.code] || { percent: 0, mastery: 'Beginner' };
                return (
                  <article className={`rounded-3xl border bg-white p-5 shadow-sm transition hover:shadow-md ${step.completed ? 'border-emerald-200' : 'border-indigo-100'}`}>
                    <div className="flex items-start gap-3">
                      <span className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-indigo-50 text-2xl" aria-hidden="true">{step.icon}</span>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <h2 className="font-black text-slate-900">{step.label}</h2>
                          <span className={`rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-wider ${step.completed ? 'bg-emerald-100 text-emerald-700' : stepProgress.percent > 0 ? 'bg-amber-100 text-amber-700' : 'bg-indigo-50 text-indigo-600'}`}>
                            {step.completed ? t('journeyPages.finished') : stepProgress.percent > 0 ? t('journeyPages.inProgress') : t('learningPaths.ready')}
                          </span>
                        </div>
                        <p className="mt-1 text-xs leading-relaxed text-slate-500">{step.desc}</p>
                      </div>
                    </div>
                    <div className="mt-4 h-2 overflow-hidden rounded-full bg-slate-100"><div className={`h-full rounded-full ${step.completed ? 'bg-emerald-500' : 'bg-indigo-500'}`} style={{ width: `${stepProgress.percent}%` }} /></div>
                    <div className="mt-2 flex justify-between text-[10px] font-bold text-slate-400"><span>{stepProgress.mastery}</span><span>{stepProgress.percent}%</span></div>
                    <div className="mt-4 grid grid-cols-2 gap-2">
                      <button type="button" onClick={() => openStep(step)} className="rounded-xl bg-indigo-600 px-3 py-2.5 text-xs font-black text-white transition hover:bg-indigo-700">
                        {t('journeyPages.openSection')}
                      </button>
                      <button type="button" onClick={() => finishStep(step)} disabled={step.completed} className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-xs font-black text-emerald-700 transition hover:bg-emerald-100 disabled:cursor-default disabled:opacity-60">
                        {step.completed ? `✓ ${t('journeyPages.finished')}` : t('journeyPages.markFinished')}
                      </button>
                    </div>
                  </article>
                );
              }}
            />
          </section>
        )}
      </main>
    </div>
  );
}
